import { useData } from "../../runtime/provider";
import { usePlayerActions } from "@utils/player/core/usePlayer";
import LBox from "@components/LBox";
import LIcon, { type IconName } from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import SaveButton from "@components/BarMusicPlayer/SaveButton";
import { ContextMenu } from "@components/Accesses/AccessDetails/components/common/More";
import { ViewArtistModalSWMSheet } from "@components/modals/ViewArtistModalSWM";
import { SelectedCollection } from "@components/OptionsMenuModal/types";
import { useMe } from "@hooks/trpc/useMe";
import { useNotinterested } from "@hooks/trpc/useNotInterested";
import { usePlaylist } from "@hooks/trpc/usePlaylists";
import { useShare } from "@hooks/trpc/useShare";
import { getActiveTab } from "@hooks/useActiveTab";
import { captureException } from "@utils/captureException";

import type { CatalogSong } from "@utils/player";
import { useActiveQueue } from "@utils/player";
import { selectShuffleOn } from "@utils/player/core/selectors";
import { usePlayer } from "@utils/player/core/usePlayer";
import { router } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { useColorScheme, useWindowDimensions } from "react-native";
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import { toast } from "sonner-native";

interface OptionsGridSWMProps {
  closeMenu: () => void;
  closeOverflow?: () => void;
  overflowButtonLayout?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  onRemoveFromQueue?: () => void;
  selectedCollection: SelectedCollection;
  selectedQueueSongCount?: number;
  showOverflow?: boolean;
  song: CatalogSong;
}

const ADD_TO_QUEUE_CONFIRMATION_MS = 400;

export const OptionsGridSWM = ({
  closeMenu,
  closeOverflow = () => {},
  overflowButtonLayout = null,
  onRemoveFromQueue = () => {},
  selectedCollection,
  selectedQueueSongCount = 0,
  showOverflow = false,
  song,
}: OptionsGridSWMProps) => {
  const session = useData();
  const playerActions = usePlayerActions();

  const addNext = playerActions.addNext;
  const activeQueue = useActiveQueue();
  const repeatMode = usePlayer((state) => state.repeatMode);
  const shuffleOn = usePlayer(selectShuffleOn);
  const bottomSheetManager = useBottomSheetManager();
  const {
    query: { data: user },
  } = useMe();

  const notInterested = useNotinterested();
  const share = useShare();
  const repeatModeIcon: IconName =
    repeatMode === "track" ? "loop_current" : "loop";
  const activePlaybackColor = "#3880E4";
  const [isAddingToQueue, setIsAddingToQueue] = useState(false);
  const artistLookupPending = useRef(false);
  const artistLookupGeneration = useRef(0);
  useEffect(
    () => () => {
      artistLookupGeneration.current += 1;
      artistLookupPending.current = false;
    },
    [song.id],
  );
  const songIsAlreadyQueued =
    activeQueue?.items.some((item) => item.metadata.id === song.id) ?? false;
  const shuffleUnavailable = selectedCollection.collectionType === "foryou";

  useEffect(() => {
    if (!isAddingToQueue || !songIsAlreadyQueued) return;

    const confirmationTimeout = setTimeout(() => {
      setIsAddingToQueue(false);
      closeMenu();
      toast.success("Added to queue", { duration: 1500 });
    }, ADD_TO_QUEUE_CONFIRMATION_MS);

    return () => clearTimeout(confirmationTimeout);
  }, [closeMenu, isAddingToQueue, songIsAlreadyQueued]);
  const { width: windowWidth } = useWindowDimensions();
  const selectedDockInset = (windowWidth - 32) * 0.94 * 0.14;
  const selectionProgress = useSharedValue(selectedQueueSongCount > 0 ? 1 : 0);

  useEffect(() => {
    selectionProgress.set(
      withTiming(selectedQueueSongCount > 0 ? 1 : 0, {
        duration: 220,
        easing: Easing.bezier(0.77, 0, 0.175, 1),
      }),
    );
  }, [selectedQueueSongCount, selectionProgress]);

  const dockBackgroundStyle = useAnimatedStyle(
    () => ({
      left: interpolate(
        selectionProgress.value,
        [0, 1],
        [0, selectedDockInset],
      ),
      right: interpolate(
        selectionProgress.value,
        [0, 1],
        [0, selectedDockInset],
      ),
    }),
    [selectedDockInset],
  );
  const dockActionsStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      selectionProgress.value,
      [0, 0.42],
      [1, 0],
      Extrapolation.CLAMP,
    ),
    transform: [
      {
        scale: interpolate(
          selectionProgress.value,
          [0, 0.42],
          [1, 0.96],
          Extrapolation.CLAMP,
        ),
      },
    ],
  }));
  const removeActionStyle = useAnimatedStyle(() => ({
    opacity: interpolate(
      selectionProgress.value,
      [0.42, 1],
      [0, 1],
      Extrapolation.CLAMP,
    ),
    transform: [
      {
        scale: interpolate(
          selectionProgress.value,
          [0.42, 1],
          [0.96, 1],
          Extrapolation.CLAMP,
        ),
      },
    ],
  }));

  const {
    query: { data: playlist },
    removeSong,
  } = usePlaylist(selectedCollection.collectionId, {
    enabled: selectedCollection.collectionType === "playlist",
  });

  const ownPlaylist = !!playlist && playlist?.user?.id === user?.id;
  const isSongInPlaylist = !!playlist?.songs?.find((s) => s.id === song.id);

  const handlePressShare = async () => {
    await share.mutateAsync({
      linkType: "direct-link",
      contentType: "song",
      data: {
        ...song,
        artistDisplayText: song.artists.map((artist) => artist.name).join(", "),
        albumId: song.albums.at(0)?.id ?? "",
        collectionId: selectedCollection.collectionId,
        collectionType: selectedCollection.collectionType,
        playlistVisibility: playlist?.visibility,
        imageUrl: song.albums.at(0)?.image ?? null,
      },
    });
  };

  const openArtist = async () => {
    if (artistLookupPending.current || song.artists.length === 0) return;
    artistLookupPending.current = true;
    const generation = artistLookupGeneration.current;
    let profiles: { id: string; image: string | null }[];
    try {
      // Resolve portraits on demand without adding identity lookups to song lists.
      const available =
        await session.api.catalog.artists.getProfileArtists.query([
          ...new Set(song.artists.map((artist) => artist.id)),
        ]);
      if (generation !== artistLookupGeneration.current) return;
      profiles = available;
    } catch (error) {
      if (generation !== artistLookupGeneration.current) return;
      captureException(error, {
        channel: "warning",
        area: "artist-navigation",
        operation: "resolve-available-artists",
      });
      toast.error("Unable to load artists. Please try again.");
      return;
    } finally {
      if (generation === artistLookupGeneration.current) {
        artistLookupPending.current = false;
      }
    }
    const profileIds = new Set(profiles.map((profile) => profile.id));
    const navigableArtists = song.artists.filter((artist) =>
      profileIds.has(artist.id),
    );
    if (!navigableArtists.length) {
      toast.error("This artist profile is unavailable");
      return;
    }
    if (navigableArtists.length === 1) {
      closeMenu();
      router.navigate({
        pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]`,
        params: { artistId: navigableArtists[0].id, collectionType: "artist" },
      });
      return;
    }
    closeMenu();
    setTimeout(() => {
      bottomSheetManager.open(
        <ViewArtistModalSWMSheet
          artists={navigableArtists.map((artist) => ({
            id: artist.id,
            image:
              profiles.find((profile) => profile.id === artist.id)?.image ??
              artist.image ??
              "",
            name: artist.name,
          }))}
        />,
        {
          mode: "push",
        },
      );
    }, 250);
  };

  const openAlbum = () => {
    closeMenu();
    router.navigate({
      pathname: `/(app)/(tabs)/${getActiveTab()}/[collectionType]/[collectionId]`,
      params: {
        collectionId: song.albums.at(0)?.id ?? "",
        collectionType: "album",
      },
    });
  };

  const openCredits = () => {
    closeMenu();
    router.navigate({
      pathname: "/(app)/[collectionType]/[collectionId]/song/[songId]/credits",
      params: {
        collectionId: song.albums.at(0)?.id ?? "",
        collectionType: "album",
        songId: song.id,
      },
    });
  };

  const markAsNotInterested = () => {
    closeMenu();

    void notInterested({ id: song.id, type: "song" });

    toast.success("Thanks for your feedback.");
  };

  const removeFromPlaylist = () => {
    removeSong
      .mutateAsync({
        id: selectedCollection.collectionId,
        songId: song.id,
      })
      .catch((error) => {
        captureException(error, {
          area: "playlist",
          channel: "error",
          operation: "remove-song-from-options-menu",
          tags: {
            playlistId: selectedCollection.collectionId,
            songId: song.id,
          },
        });
      });

    closeMenu();
  };

  const addToQueue = async () => {
    if (isAddingToQueue || songIsAlreadyQueued) return;

    setIsAddingToQueue(true);
    try {
      await addNext(song.id);
    } catch (error) {
      setIsAddingToQueue(false);
      throw error;
    }
  };

  const dockActions: Array<{
    label: string;
    disabled?: boolean;
    selected?: boolean;
    icon: IconName;
    onPress: () => void;
    sentryEventName: string;
    tint?: string;
  }> = [
    {
      disabled: songIsAlreadyQueued || isAddingToQueue,
      label: "Add to queue",
      icon: "add_queue",
      onPress: addToQueue,
      sentryEventName: "add_to_queue",
    },
    {
      disabled: shuffleUnavailable,
      label: "Shuffle",
      icon: "shuffle",
      onPress: () => playerActions.setShuffleOn(!shuffleOn),
      sentryEventName: "shuffle_swm",
      selected: shuffleOn,
      tint: shuffleOn ? activePlaybackColor : "white",
    },
    {
      label: `Repeat ${repeatMode}`,
      icon: repeatModeIcon,
      onPress: playerActions.cycleRepeatMode,
      sentryEventName: "repeat_swm",
      tint: repeatMode === "off" ? "white" : activePlaybackColor,
    },
    {
      disabled: share.isPending,
      label: "Share song",
      icon: "share",
      onPress: handlePressShare,
      sentryEventName: "share_song",
    },
  ];

  return (
    <LBox>
      <ContextMenu
        buttonLayout={overflowButtonLayout}
        onClose={closeOverflow}
        options={[
          {
            content: (
              <OverflowActionContent icon="album_view" label="View Album" />
            ),
            key: "view_album",
            onPress: openAlbum,
          },
          {
            content: (
              <OverflowActionContent icon="view_artist" label="View Artist" />
            ),
            key: "view_artist_individual",
            onPress: openArtist,
          },
          {
            content: <OverflowActionContent icon="info" label="View Credits" />,
            key: "view_credits",
            onPress: openCredits,
          },
          ...(ownPlaylist && isSongInPlaylist
            ? [
                {
                  content: (
                    <OverflowActionContent
                      icon="remove_from_playlist"
                      label="Remove from Playlist"
                    />
                  ),
                  key: "remove_from_playlist",
                  onPress: removeFromPlaylist,
                },
              ]
            : []),
          {
            content: (
              <OverflowActionContent
                danger
                icon="cross"
                label="Not Interested"
              />
            ),
            key: "not_interested",
            onPress: markAsNotInterested,
          },
        ]}
        placement="below"
        visible={showOverflow}
      />
      <LBox
        alignItems="center"
        alignSelf="center"
        mt="s2"
        style={{
          height: 56,
          minHeight: 56,
          position: "relative",
          transform: [{ translateY: 8 }],
        }}
        width="94%"
      >
        <Animated.View
          pointerEvents="none"
          style={[
            {
              backgroundColor: "#333333",
              borderRadius: 999,
              bottom: 0,
              left: 0,
              position: "absolute",
              right: 0,
              top: 0,
            },
            dockBackgroundStyle,
          ]}
        />
        <Animated.View
          pointerEvents={selectedQueueSongCount > 0 ? "none" : "auto"}
          style={[
            {
              alignItems: "center",
              bottom: 0,
              flexDirection: "row",
              justifyContent: "space-around",
              left: 0,
              paddingHorizontal: 12,
              position: "absolute",
              right: 0,
              top: 0,
            },
            dockActionsStyle,
          ]}
        >
          {dockActions.slice(0, 3).map((action) => (
            <DockIconAction action={action} key={action.sentryEventName} />
          ))}
          <SaveButton
            song={song}
            style={{
              alignItems: "center",
              height: 32,
              justifyContent: "center",
              paddingHorizontal: 0,
              width: 32,
            }}
          />
          {dockActions.slice(3).map((action) => (
            <DockIconAction action={action} key={action.sentryEventName} />
          ))}
        </Animated.View>
        <Animated.View
          pointerEvents={selectedQueueSongCount > 0 ? "auto" : "none"}
          style={[
            {
              alignItems: "center",
              bottom: 0,
              justifyContent: "center",
              left: "14%",
              position: "absolute",
              right: "14%",
              top: 0,
            },
            removeActionStyle,
          ]}
        >
          <LPressable
            onPress={onRemoveFromQueue}
            sentryEventName="queue_screen_remove_from_queue_swm"
            style={{
              alignItems: "center",
              alignSelf: "stretch",
              flex: 1,
              justifyContent: "center",
            }}
          >
            <LText color="white" variant="bodyM2m">
              REMOVE FROM QUEUE
            </LText>
          </LPressable>
        </Animated.View>
      </LBox>
    </LBox>
  );
};

const DockIconAction = ({
  action,
}: {
  action: {
    label: string;
    disabled?: boolean;
    selected?: boolean;
    icon: IconName;
    onPress: () => void;
    sentryEventName: string;
    tint?: string;
  };
}) => (
  <LPressable
    accessibilityLabel={action.label}
    accessibilityRole="button"
    accessibilityState={{
      disabled: action.disabled,
      selected: action.selected,
    }}
    disabled={action.disabled}
    hitSlop={10}
    onPress={action.onPress}
    sentryEventName={action.sentryEventName}
    style={{
      alignItems: "center",
      height: 32,
      justifyContent: "center",
      width: 32,
    }}
  >
    <LIcon
      colors={{
        color: action.disabled ? "greyText" : action.tint ?? "white",
      }}
      name={action.icon}
      size={26}
    />
  </LPressable>
);

const OverflowActionContent = ({
  danger = false,
  icon,
  label,
}: {
  danger?: boolean;
  icon: IconName;
  label: string;
}) => {
  const isDark = useColorScheme() === "dark";
  const color = danger ? "danger" : isDark ? "white" : "black";

  return (
    <LBox alignItems="center" flexDirection="row" gap="s3">
      <LIcon colors={{ color }} name={icon} size={22} />
      <LText color={color} variant="bodyM2m">
        {label}
      </LText>
    </LBox>
  );
};
